import type { StreamEvent, SyncMode } from "@/types/sync.types";
import { t } from "@lingui/core/macro";

export function getSyncStatusMessage(evt: StreamEvent, mode: SyncMode): string {
  const modeLabel =
    mode === "full_refresh"
      ? t`Full refresh`
      : mode === "force_fetch"
        ? t`Force fetch`
        : t`Normal sync`;

  switch (evt.type) {
    case "start":
      return t`Starting ${modeLabel}…`;

    case "skipped": {
      const age = evt.details?.age_minutes;
      return typeof age === "number"
        ? t`Skipped (last sync was ${age} minutes ago).`
        : t`Skipped (synced recently).`;
    }

    case "info": {
      switch (evt.code) {
        case "sync.lock.acquired":
          return t`Acquired sync lock.`;

        case "sync.token_refresh.start":
          return t`Refreshing Strava token…`;

        case "sync.token_refresh.done":
          return t`Token refreshed.`;

        case "sync.full_refresh.delete_start":
          return t`Deleting local activities…`;

        case "sync.full_refresh.delete_done":
          return t`Local activities deleted.`;

        case "sync.cutoff.set": {
          const overlap = (evt.details as any)?.overlap_days as
            | number
            | undefined;
          return typeof overlap === "number"
            ? t`Scanning with ${overlap}-day overlap…`
            : t`Preparing scan window…`;
        }

        case "sync.page.fetched": {
          const d = evt.details as
            | { pages_fetched?: number; activities_fetched?: number }
            | undefined;
          const pages = d?.pages_fetched ?? 0;
          const acts = d?.activities_fetched ?? 0;
          return t`Fetched ${acts} activities (${pages} pages).`;
        }

        case "sync.cutoff.reached":
          return t`Reached scan cutoff date.`;

        case "sync.fetch.done":
          return t`Finished fetching activities.`;

        case "sync.persist.start":
          return t`Saving activities…`;

        case "sync.persist.upsert_done": {
          const upserted = (evt.details as any)?.upserted as number | undefined;
          return typeof upserted === "number"
            ? t`Saved ${upserted} activities.`
            : t`Saved activities.`;
        }

        case "sync.deletions_scan.start":
          return t`Checking for deleted activities…`;

        case "sync.deletions_scan.done": {
          const marked = (evt.details as any)?.marked_deleted as
            | number
            | undefined;
          return typeof marked === "number"
            ? t`Marked ${marked} activities as deleted.`
            : t`Deletion check done.`;
        }

        case "sync.connection.updated":
          return t`Updated sync metadata.`;

        case "sync.quota.updated":
          return t`Updated daily quota.`;

        default:
          return t`Sync in progress…`;
      }
    }

    case "warning": {
      switch (evt.code) {
        case "sync.lock.busy":
          return t`Sync already running in another session.`;

        case "sync.quota.reached": {
          const limit = (evt.details as any)?.limit as number | undefined;
          return typeof limit === "number"
            ? t`Daily API quota reached (${limit}).`
            : t`Daily API quota reached.`;
        }

        case "sync.warning.max_pages": {
          const max = (evt.details as any)?.max_pages as number | undefined;
          return typeof max === "number"
            ? t`Reached maximum pages (${max}). Some activities may be missing.`
            : t`Reached maximum pages. Some activities may be missing.`;
        }

        default:
          return t`Sync warning.`;
      }
    }

    case "error":
      return t`Sync failed.`;

    case "done":
      return evt.ok ? t`Sync complete.` : t`Sync finished with errors.`;

    default:
      return t`Sync…`;
  }
}
